import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import {
  DISPUTE_TYPE,
  formatAssociatedMovement,
} from "@features/vessel-call/vessel-call-status/Operations/grid/OperationsDisputes.mapper";
import { useSubmitDispute } from "@features/vessel-call/vessel-call-status/Operations/hooks/useSubmitDispute";
import { useSubmitNoDispute } from "@features/vessel-call/vessel-call-status/Operations/hooks/useSubmitNoDispute";
import { useUpdateDispute } from "@features/vessel-call/vessel-call-status/Operations/hooks/useUpdateDispute";
import { useUploadOperationsDocuments } from "@features/vessel-call/vessel-call-status/Operations/hooks/useUploadOperationsDocuments";
import {
  DISPUTE_DOCUMENT_FIELD,
  DISPUTE_DOCUMENTS_MAX_FILES,
  DISPUTE_REASON_MAX_LENGTH,
  getDisputeDrawerSchema,
} from "@features/vessel-call/vessel-call-status/Operations/schema/disputeDrawerSchema";
import {
  getNoDisputeDrawerSchema,
  NO_DISPUTE_COMMENTS_MAX_LENGTH,
  NO_DISPUTE_DOCUMENT_FIELD,
  NO_DISPUTE_DOCUMENTS_MAX_FILES,
} from "@features/vessel-call/vessel-call-status/Operations/schema/noDisputeDrawerSchema";
import type {
  DisputeDrawerFormValues,
  NoDisputeDrawerFormValues,
} from "@features/vessel-call/vessel-call-status/Operations/types/types";
import { zodResolver } from "@hookform/resolvers/zod";
import type { DisputeDocument } from "@services/disputes/types";
import { getEligibleAssociatedMovements } from "@services/disputesApi";
import { useQuery } from "@tanstack/react-query";
import { runInquiry } from "@telemetry/runInquiry";
import { FormProvider, useForm, type Resolver } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

export type OperationsDisputeDrawerMode = "dispute" | "no-dispute" | "update-dispute";

type OperationsDisputeFormValues = DisputeDrawerFormValues & NoDisputeDrawerFormValues;

type DisputeDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  mode?: OperationsDisputeDrawerMode;
  disputeId?: string | null;
};

const LANG_BASE = "vessel-call.operations";
const TEST_ID_BASE = "operations-dispute";
const DISPUTE_DOCUMENT_CERTIFICATE_TYPE = "DISPUTE_DOCUMENT";
const ASSOCIATED_MOVEMENTS_INQUIRY_CONTEXT = {
  module: "vessel_call",
  screen: "Operations",
  action: "load_eligible_associated_movements",
  entityType: "movement",
};

const defaultValues: OperationsDisputeFormValues = {
  associatedMovementId: "",
  disputeReason: "",
  comments: "",
  [DISPUTE_DOCUMENT_FIELD]: [],
  [NO_DISPUTE_DOCUMENT_FIELD]: [],
};

const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];

const toDisputeDocuments = (entries: UploadEntry[]): DisputeDocument[] => {
  const documents: DisputeDocument[] = [];

  entries.forEach((entry) => {
    if (entry instanceof File || typeof entry.url !== "string" || !entry.url.trim()) {
      return;
    }

    if (typeof entry.name !== "string" || !entry.name.trim()) {
      return;
    }

    documents.push({
      originalFileName: entry.name.trim(),
      url: entry.url.trim(),
    });
  });

  return documents;
};

const DisputeDrawer = ({ isOpen, onClose, mode = "dispute", disputeId }: DisputeDrawerProps) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { id: vesselCallId = "" } = useParams();

  const isNoDispute = mode === "no-dispute";
  const isUpdateDispute = mode === "update-dispute";
  const isChangingExistingDispute = isNoDispute && Boolean(disputeId);
  const isCreatingDeclaration = !isUpdateDispute && !isChangingExistingDispute;
  const documentField = isNoDispute ? NO_DISPUTE_DOCUMENT_FIELD : DISPUTE_DOCUMENT_FIELD;
  const maxDocuments = isNoDispute ? NO_DISPUTE_DOCUMENTS_MAX_FILES : DISPUTE_DOCUMENTS_MAX_FILES;
  const certificateType = isNoDispute ? "NO_DISPUTE_DOCUMENT" : DISPUTE_DOCUMENT_CERTIFICATE_TYPE;
  const detailsField = isNoDispute ? "comments" : "disputeReason";
  const testIdBase = isNoDispute
    ? "operations-no-dispute"
    : isUpdateDispute
      ? "operations-update-dispute"
      : TEST_ID_BASE;

  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const { data: eligibleMovements = [], isLoading: isMovementsLoading } = useQuery({
    queryKey: ["eligible-associated-movements", vesselCallId],
    queryFn: () =>
      runInquiry(ASSOCIATED_MOVEMENTS_INQUIRY_CONTEXT, () =>
        getEligibleAssociatedMovements({ service: "bff-vessel-call", vesselCallId }),
      ),
    enabled: isOpen && isCreatingDeclaration && Boolean(vesselCallId),
  });
  const eligibleMovementOptions = eligibleMovements.map((movement) => ({
    value: movement.id,
    label: formatAssociatedMovement(movement.displayName),
  }));
  const uploadSessionRef = useRef(0);
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);
  const removeFileAtIndex = useUploadStore((state) => state.removeFileAtIndex);

  const disputeDocuments = useUploadStore(
    (state) => state.filesByKey[documentField] ?? EMPTY_UPLOAD_ENTRIES,
  );
  const isDisputeDocumentUploading = useUploadStore(
    (state) => (state.uploadsInFlightByKey[documentField] ?? 0) > 0,
  );

  const schema = useMemo(
    () =>
      isNoDispute
        ? getNoDisputeDrawerSchema(t, isCreatingDeclaration)
        : getDisputeDrawerSchema(t, isCreatingDeclaration),
    [isCreatingDeclaration, isNoDispute, t],
  );
  const uploadConditions = isNoDispute
    ? FILE_UPLOAD_PROFILES.noDisputeDocument
    : FILE_UPLOAD_PROFILES.disputeDocument;

  const resolverRef = useRef<Resolver<OperationsDisputeFormValues>>(
    zodResolver(schema as never) as Resolver<OperationsDisputeFormValues>,
  );
  resolverRef.current = zodResolver(schema as never) as Resolver<OperationsDisputeFormValues>;

  const resolver = useCallback<Resolver<OperationsDisputeFormValues>>(
    (...args) => resolverRef.current(...args),
    [],
  );

  const form = useForm<OperationsDisputeFormValues>({
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver,
    shouldUnregister: true,
    defaultValues,
  });

  const setDisputeFormDocuments = useCallback(
    (documents: DisputeDocument[]) => {
      form.setValue(documentField, documents, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [documentField, form],
  );

  const syncFormDocumentsFromStore = useCallback(() => {
    const currentEntries = useUploadStore.getState().getFilesForKey(documentField);
    setDisputeFormDocuments(toDisputeDocuments(currentEntries));
  }, [documentField, setDisputeFormDocuments]);

  useEffect(() => {
    clearFilesForKey(documentField);
    form.setValue(documentField, []);
  }, [clearFilesForKey, documentField, form]);

  const setDisputeDocuments = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey(documentField);
        setDisputeFormDocuments([]);
        return;
      }

      setFilesForKey(documentField, files, {
        maxFiles: maxDocuments,
      });
      setDisputeFormDocuments(toDisputeDocuments(files));
    },
    [clearFilesForKey, documentField, maxDocuments, setFilesForKey, setDisputeFormDocuments],
  );

  const applyUploadedDocuments = useCallback(
    (documentUrls: string[], startIdx: number, uploadedFiles: File[]) => {
      const currentEntries = useUploadStore.getState().getFilesForKey(documentField);
      const nextEntries = [...currentEntries];

      documentUrls.forEach((documentUrl, index) => {
        if (!documentUrl) return;

        const targetIndex = startIdx + index;
        const currentEntry = nextEntries[targetIndex];
        const uploadedFile = uploadedFiles[index];

        nextEntries[targetIndex] = {
          name: currentEntry?.name ?? uploadedFile?.name ?? `document-${targetIndex + 1}`,
          url: documentUrl,
          type: currentEntry instanceof File ? currentEntry.type : currentEntry?.type,
          certificateType,
          idx: targetIndex,
        };
      });

      setFilesForKey(documentField, nextEntries, {
        maxFiles: maxDocuments,
      });
      setDisputeFormDocuments(toDisputeDocuments(nextEntries));
    },
    [certificateType, documentField, maxDocuments, setFilesForKey, setDisputeFormDocuments],
  );

  const handleUploadError = useCallback(
    (startIdx: number, count: number) => {
      notify({
        title: t("vessel-management.notifications.attachments-upload-error"),
        description: t("vessel-management.notifications.attachments-upload-error-description"),
        type: "error",
      });

      const indexesToRemove = Array.from(
        { length: count },
        (_value, index) => startIdx + index,
      ).reverse();

      indexesToRemove.forEach((index) => {
        removeFileAtIndex(documentField, index);
      });
      syncFormDocumentsFromStore();
    },
    [documentField, notify, removeFileAtIndex, syncFormDocumentsFromStore, t],
  );

  const { mutate: uploadDisputeDocuments } = useUploadOperationsDocuments();

  const allowedFileExtensions = useMemo(
    () =>
      (uploadConditions.format ?? []).map((extension) =>
        extension.startsWith(".") ? extension.toLowerCase() : `.${extension.toLowerCase()}`,
      ),
    [uploadConditions.format],
  );
  const uploadDescription = t("upload.additional-documents-upload", {
    extensions: allowedFileExtensions.join(", "),
    size: uploadConditions.maxSizeLabel,
  });

  const resetDrawer = useCallback(() => {
    uploadSessionRef.current += 1;
    setIsConfirmDialogOpen(false);
    clearFilesForKey(documentField);
    form.reset(defaultValues);
  }, [clearFilesForKey, documentField, form]);

  const handleClose = useCallback(() => {
    resetDrawer();
    onClose();
  }, [onClose, resetDrawer]);

  const { submitDispute, isSubmittingDispute } = useSubmitDispute();
  const { submitNoDispute, isSubmittingNoDispute } = useSubmitNoDispute();
  const { updateDispute, isUpdatingDispute } = useUpdateDispute();

  const validateUploadFiles = useCallback(
    (incomingFiles: File[]) => {
      const existingFilesCount =
        useUploadStore.getState().getFilesForKey(documentField).length ?? 0;

      if (existingFilesCount + incomingFiles.length > maxDocuments) {
        return t("form-validation.max-files", { max: maxDocuments });
      }

      return null;
    },
    [documentField, maxDocuments, t],
  );

  const validateDrop = useCallback(
    (selectedCount: number) => {
      if (disputeDocuments.length + selectedCount > maxDocuments) {
        return t("form-validation.max-files", { max: maxDocuments });
      }

      return null;
    },
    [disputeDocuments.length, maxDocuments, t],
  );

  const handleDisputeDocumentUpload = useCallback(
    (_fieldName: typeof documentField, uploadedFiles: File[]) => {
      const incomingFiles = Array.isArray(uploadedFiles)
        ? uploadedFiles.filter((entry): entry is File => entry instanceof File)
        : [];
      const currentEntries = useUploadStore.getState().getFilesForKey(documentField);

      setDisputeFormDocuments(toDisputeDocuments(currentEntries));

      if (!incomingFiles.length) return;

      const firstFileIndex = currentEntries.findIndex((entry) => entry === incomingFiles[0]);
      const fallbackStartIdx = Math.max(currentEntries.length - incomingFiles.length, 0);
      const startIdx = firstFileIndex >= 0 ? firstFileIndex : fallbackStartIdx;
      const uploadSession = uploadSessionRef.current;

      uploadDisputeDocuments(
        {
          files: incomingFiles,
          certificateType,
          storeKey: documentField,
        },
        {
          onSuccess: (documentUrls) => {
            if (uploadSession !== uploadSessionRef.current) return;
            applyUploadedDocuments(documentUrls, startIdx, incomingFiles);
          },
          onError: () => {
            if (uploadSession !== uploadSessionRef.current) return;
            handleUploadError(startIdx, incomingFiles.length);
          },
        },
      );
    },
    [
      applyUploadedDocuments,
      handleUploadError,
      certificateType,
      documentField,
      setDisputeFormDocuments,
      uploadDisputeDocuments,
    ],
  );

  const handleSubmit = () => {
    if (isUpdateDispute) {
      form.handleSubmit(() => {
        handleConfirmDialogConfirm();
      })();
      return;
    }

    form.handleSubmit(() => {
      setIsConfirmDialogOpen(true);
    })();
  };

  const handleConfirmDialogClose = () => {
    setIsConfirmDialogOpen(false);
  };

  const handleConfirmDialogConfirm = () => {
    if (
      isUpdateDispute || isChangingExistingDispute
        ? isUpdatingDispute
        : isNoDispute
          ? isSubmittingNoDispute
          : isSubmittingDispute
    )
      return;

    const values = form.getValues();

    if (!isUpdateDispute && !isChangingExistingDispute && !vesselCallId) {
      notify({
        title: t("common.error"),
        description: t("common.something-went-wrong"),
        type: "error",
      });
      return;
    }

    const disputeDetails = values[detailsField]?.trim() ?? "";
    const documents = values[documentField] ?? [];

    if (isUpdateDispute || isChangingExistingDispute) {
      if (!disputeId) {
        notify({
          title: t("common.error"),
          description: t("common.something-went-wrong"),
          type: "error",
        });
        return;
      }

      updateDispute(
        {
          disputeId,
          payload: {
            action: isChangingExistingDispute ? DISPUTE_TYPE.NO_DISPUTE : null,
            disputeDetails,
            documents,
          },
        },
        { onSuccess: handleClose },
      );
    } else {
      (isNoDispute ? submitNoDispute : submitDispute)(
        {
          vesselCallId,
          associatedMovementId: values.associatedMovementId ?? "",
          type: isNoDispute ? DISPUTE_TYPE.NO_DISPUTE : DISPUTE_TYPE.DISPUTE,
          disputeDetails,
          documents,
        },
        { onSuccess: handleClose },
      );
    }
    setIsConfirmDialogOpen(false);
  };

  const isSubmitting = isNoDispute
    ? isChangingExistingDispute
      ? isUpdatingDispute
      : isSubmittingNoDispute
    : isUpdateDispute
      ? isUpdatingDispute
      : isSubmittingDispute;

  return (
    <div data-testid={`${testIdBase}-drawer`}>
      <FormProvider {...form}>
        <SideDrawer
          isOpen={isOpen}
          onClose={handleClose}
          onCancel={handleClose}
          onSubmit={handleSubmit}
          isSubmitDisabled={isDisputeDocumentUploading || isSubmitting}
          keepMounted={false}
          title={t(
            `${LANG_BASE}.${isNoDispute ? "no-dispute-title" : isUpdateDispute ? "update-dispute" : "dispute-title"}`,
          )}
          submitLabel={t(
            `${LANG_BASE}.${isNoDispute ? "no-dispute" : isUpdateDispute ? "update-dispute" : "raise-dispute"}`,
          )}
          cancelLabel={t("common.cancel")}
        >
          <div className="w-[400px] p-6 flex flex-col gap-6">
            {isCreatingDeclaration && (
              <FormItem<OperationsDisputeFormValues, "associatedMovementId">
                name="associatedMovementId"
                type="select"
                label={t(`${LANG_BASE}.associated-movement`)}
                required
                testId={`${testIdBase}-associated-movement`}
                disabled={isMovementsLoading}
                options={eligibleMovementOptions}
              />
            )}
            <FormItem<OperationsDisputeFormValues, "comments" | "disputeReason">
              name={detailsField}
              type="textarea"
              label={t(`${LANG_BASE}.${isNoDispute ? "no-dispute-comments" : "dispute-reason"}`)}
              required={!isNoDispute}
              testId={`${testIdBase}-${isNoDispute ? "comments" : "dispute-reason"}`}
              minRows={4}
              maxChars={isNoDispute ? NO_DISPUTE_COMMENTS_MAX_LENGTH : DISPUTE_REASON_MAX_LENGTH}
              showCharCounter
            />
            <div className="flex flex-col gap-4">
              <FormItem<OperationsDisputeFormValues, typeof documentField>
                name={documentField}
                type="file"
                label={t(
                  `${LANG_BASE}.${isNoDispute ? "no-dispute-document" : "dispute-documents"}`,
                )}
                testId={`${testIdBase}-documents`}
                allowedFileExtensions={allowedFileExtensions}
                uploadConditions={uploadConditions}
                maxFiles={maxDocuments}
                files={disputeDocuments}
                setFiles={setDisputeDocuments}
                uploadDescription={uploadDescription}
                onUpload={handleDisputeDocumentUpload}
                fileValidation={validateUploadFiles}
                notSuccess
                validateDrop={validateDrop}
                disabled={isDisputeDocumentUploading}
              />
              {disputeDocuments.map((file, index) => (
                <SingleFile
                  key={`${file.name}-${index}`}
                  testId={`${testIdBase}-document-${index}`}
                  file={file}
                  fieldName={documentField}
                  currentFileIndex={index}
                  setFiles={() => {}}
                  removeFn={syncFormDocumentsFromStore}
                  isLoading={isDisputeDocumentUploading && file instanceof File}
                />
              ))}
            </div>
          </div>
        </SideDrawer>
      </FormProvider>
      <GenericDialog
        open={isConfirmDialogOpen && !isUpdateDispute}
        title={t(
          `${LANG_BASE}.${isNoDispute ? "no-dispute-confirm-title" : "dispute-confirm-title"}`,
        )}
        elementContent={
          <span className="whitespace-pre-line">
            <Trans
              i18nKey={`${LANG_BASE}.${isNoDispute ? "no-dispute-confirm-message" : "dispute-confirm-message"}`}
              components={{ strong: <strong /> }}
            />
          </span>
        }
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t(`${LANG_BASE}.${isNoDispute ? "no-dispute" : "raise-dispute"}`)}
        handleCloseDialog={handleConfirmDialogClose}
        confirmAction={handleConfirmDialogConfirm}
        testId={`${testIdBase}-confirm-dialog`}
        isInsideDrawer
      />
    </div>
  );
};

export default DisputeDrawer;
